import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Aide } from "@/components/ui/aide"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = ({ className, ref, ...props }) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = ({ className, children, ref, ...props }) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[92vh] w-[calc(100vw-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto overflow-x-hidden overscroll-contain border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
)
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * Texte d'un enfant quand il n'est fait que de texte (sinon null): c'est ce
 * qui permet de mesurer une description et de la ranger derrière l'aide.
 */
const texteSeul = (enfants) => {
  const liste = React.Children.toArray(enfants)
  if (liste.length === 0) return null
  if (!liste.every((enfant) => typeof enfant === "string" || typeof enfant === "number")) return null
  return liste.join("").replace(/\s+/g, " ").trim()
}

/** Au-delà de ce nombre de mots, la description d'une fenêtre est une explication. */
const SEUIL_MOTS_DESCRIPTION = 8

const DialogHeader = ({
  className,
  children,
  ...props
}) => {
  // Une description qui explique « comment ça marche » ne reste pas affichée
  // sous le titre: elle passe derrière une icône d'aide à côté du titre. Elle
  // reste dans la fenêtre (masquée à l'écran) pour les lecteurs d'écran.
  const liste = React.Children.toArray(children)
  const iTitre = liste.findIndex((c) => React.isValidElement(c) && c.type === DialogTitle)
  const iDescription = liste.findIndex(
    (c) => React.isValidElement(c) && c.type === DialogDescription && !/sr-only/.test(c.props.className || "")
  )
  let contenu = children
  if (iTitre >= 0 && iDescription >= 0) {
    const texte = texteSeul(liste[iDescription].props.children)
    if (texte && texte.split(" ").length > SEUIL_MOTS_DESCRIPTION) {
      const titre = texteSeul(liste[iTitre].props.children)
      contenu = liste.map((enfant, index) => {
        if (index === iTitre) {
          return (
            <div key={enfant.key ?? "titre-aide"} className="flex items-center justify-center gap-1 sm:justify-start">
              {enfant}
              <Aide titre={titre || "Cette fenêtre"}>{texte}</Aide>
            </div>
          )
        }
        if (index === iDescription) {
          return React.cloneElement(enfant, { className: cn(enfant.props.className, "sr-only") })
        }
        return enfant
      })
    }
  }
  return (
    <div
      className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
      {...props}>
      {contenu}
    </div>
  )
}
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = ({ className, ref, ...props }) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = ({ className, ref, ...props }) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
)
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
